// src/hooks/useBillingQueries.js
// React Query wrappers for Billing service

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPlan,
  getUsage,
  getBillingInvoices,
  startCheckout,
  openPortal,
  // NEW: health + resync helpers
  getBillingHealth,
  resyncBillingHealth,
} from '../services/billingService';

const qk = {
  plan: ['billing', 'plan'],        // now effectively "summary"
  usage: ['billing', 'usage'],
  invoices: ['billing', 'invoices'],
  health: ['billing', 'health'],    // NEW
};

/** Plan / summary info */
export function usePlan(options = {}) {
  return useQuery({
    queryKey: qk.plan,
    queryFn: getPlan, // under the hood this should call /billing/summary
    staleTime: 60 * 1000, // 1 min
    gcTime: 5 * 60 * 1000,
    ...options,
  });
}

/** Usage snapshot */
export function useUsage(options = {}) {
  return useQuery({
    queryKey: qk.usage,
    queryFn: getUsage,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
}

/** Optional: list of billing invoices */
export function useBillingInvoices(options = {}) {
  return useQuery({
    queryKey: qk.invoices,
    queryFn: getBillingInvoices,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
}

/** Billing health snapshot (Stripe ↔ DB sanity) */
export function useBillingHealth(options = {}) {
  return useQuery({
    queryKey: qk.health,
    queryFn: getBillingHealth,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Start checkout → returns { url }. Callers should redirect:
 *   const { mutate: checkout } = useStartCheckout({
 *     onSuccess: ({ url }) => (window.location.href = url),
 *   });
 */
export function useStartCheckout(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: startCheckout,
    onSuccess: async (res, variables, ctx) => {
      // Best-effort refresh plan/usage after returning from Checkout
      // (Actual update is via webhook; these invalidations help after redirects)
      await Promise.allSettled([
        qc.invalidateQueries({ queryKey: qk.plan }),
        qc.invalidateQueries({ queryKey: qk.usage }),
        qc.invalidateQueries({ queryKey: qk.health }),
      ]);
      options?.onSuccess?.(res, variables, ctx);
    },
    ...options,
  });
}

/**
 * Open portal → returns { url }. Callers should redirect:
 *   const { mutate: portal } = useOpenPortal({
 *     onSuccess: ({ url }) => (window.location.href = url),
 *   });
 */
export function useOpenPortal(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: openPortal,
    onSuccess: async (res, variables, ctx) => {
      // Invalidate after portal actions when user returns
      await Promise.allSettled([
        qc.invalidateQueries({ queryKey: qk.plan }),
        qc.invalidateQueries({ queryKey: qk.usage }),
        qc.invalidateQueries({ queryKey: qk.health }),
      ]);
      options?.onSuccess?.(res, variables, ctx);
    },
    ...options,
  });
}

/**
 * Force resync of billing health / subscription from Stripe.
 * Used by "Fix now" button when health status is red/amber.
 */
export function useResyncBillingHealth(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: resyncBillingHealth,
    onSuccess: async (res, variables, ctx) => {
      await Promise.allSettled([
        qc.invalidateQueries({ queryKey: qk.plan }),
        qc.invalidateQueries({ queryKey: qk.usage }),
        qc.invalidateQueries({ queryKey: qk.invoices }),
        qc.invalidateQueries({ queryKey: qk.health }),
      ]);
      options?.onSuccess?.(res, variables, ctx);
    },
    ...options,
  });
}
